import { createClient, createQualityManager, PlaycastClientPeerStatus } from '@playcastdotio/Peer';

import { PlaycastPeerClient } from '@playcastdotio/Peer';
import { generators, PeerIdType, PeerMessageType, websocket } from '@playcastdotio/Messaging';
import { sendMetricPlayerFrameratePanic, sendMetricPlayerBatchWebrtcStats, WebrtcQualityStatsMetric } from '../../metrics';
import { isAppleDevice, isMobileDevice } from '../../utility/utility';
import * as realtime from './realtimeMessaging';
import { getPersistentPlayerId } from '..';
import { handleHostMessage } from './hostMessaging/handleMessages';
import { invitationState, playerNameState } from '../stores/lobbyStores';
import { authStore, joinState } from '../stores/globalStores';
import { playerConnectionState, mediaStreamState } from '../stores/playerStores';
// Local duplicate of diagnostic writer to avoid cross-package import issues
const recordDisconnectReason = (reason: string, context?: string) => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    const key = 'playcast.peer.disconnectHistory';
    const now = new Date().toLocaleString();
    const raw = window.localStorage.getItem(key);
    const history: Array<{ at: string; reason: string; context?: string }> = raw ? JSON.parse(raw) : [];
    history.unshift({ at: now, reason, context });
    window.localStorage.setItem(key, JSON.stringify(history.slice(0, 20)));
  } catch (err) {
    console.warn('[Marketing] Failed to record disconnect reason', err);
  }
};

const sendVideoTransitTimingData = (stage: 'player_receive') => {
  const baseMessage = {
    header: {
      source: 'player' as const,
      target: 'system' as const,
      action: 'videoTransitTiming' as const,
      isReply: false,
      hostId: '',
      userAuth: authStore.currentState.playerAuthId,
    },
    body: {
      message: {},
      timings: [],
      stats: [],
      echo: false,
    },
    signature: '',
  } as any;

  const messageWithTiming = generators.addTiming(
    baseMessage,
    {
      timestamp: performance.now(),
      description: `video_transit_${stage}`,
    },
    'player',
    {
      stage: stage,
      transit_timestamp: performance.now().toString(),
    }
  );

  console.log('[Player] Video transit timing data:', {
    stage,
    timestamp: Date.now(),
    timingMessage: messageWithTiming,
  });

  // TODO: send timing data to BigQuery
};

let peer: PlaycastPeerClient;

export const qualityManager = createQualityManager();

const PANIC_COUNT_THRESHOLD = 3;
const PANIC_HARD_RESET_TIMEOUT = 4000;
let frameratePanicCount = 0;
let frameratePanicTimeout: NodeJS.Timeout | undefined;
qualityManager.onFrameratePanic((reason) => {
  console.error('Framerate Panic!');
  if (peer) {
    // check we actually have tracks
    let hasTracks = false;
    for (const trackId in peer.incomingTracks.currentState) {
      const track = peer.incomingTracks.currentState[trackId];
      if (track.metadata.type === 'video') {
        hasTracks = true;
        break;
      }
    }

    if (!hasTracks) {
      frameratePanicCount = 0;
      return;
    }

    frameratePanicCount++;
    const hardReset = frameratePanicCount > PANIC_COUNT_THRESHOLD;
    const panicMessage = generators.peer.frameratePanic('player', peer.peerId, hardReset);
    peer.sendMessage(panicMessage);
    sendMetricPlayerFrameratePanic(hardReset, reason);
    // reset the timeout
    if (frameratePanicTimeout) {
      clearTimeout(frameratePanicTimeout);
    }
    frameratePanicTimeout = setTimeout(() => {
      frameratePanicCount = 0;
    }, PANIC_HARD_RESET_TIMEOUT);
  }
});

qualityManager.preferredQualityState.addListener((state) => {
  if (peer) {
    console.log('Changing quality to', state.profile);
    peer.changeQualityProfile(state.profile);
  }
});

export const createPeer = async (hostBroadcastId: string) => {
  if (!hostBroadcastId) {
    console.error('No host broadcast id');
    return;
  }
  if (peer) {
    if (peer.status.currentState.connectionState === 'signalling') {
      console.warn('Peer is already connecting');
      return;
    }
    console.error('Closing existing peer');
    // clear the listeners so we dont get the status update
    peer.status.removeListener();
    peer.close('player:close:duplicate_peer');
  }
  playerConnectionState.updateState({
    ...playerConnectionState.currentState,
    status: 'connecting',
    playerPeerId: '',
  });
  const playerId = await new Promise<string>((resolve) => {
    realtime.sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
        peerIdType: PeerIdType.PLAYER,
      }),
    );
    const timeout = setTimeout(() => {
      throw new Error('Peer ID timeout');
    }, 5000);
    const listener = playerConnectionState.addListener((state) => {
      if (state.playerPeerId !== '') {
        clearTimeout(timeout);
        playerConnectionState.removeListener(listener);
        resolve(state.playerPeerId);
      }
    });
  });

  // Get ICE servers from the state
  const iceServers = playerConnectionState.currentState.iceServers;

  peer = createClient(
    {
      realtimeApiManager: {
        send: (message) => {
          realtime.sendMessage(message);
        },
      },
      additionalMetadata: {
        displayName: playerNameState.currentState.name,
        playerPersistentId: getPersistentPlayerId(),
        email: authStore.currentState.email,
        playerAuthId: authStore.currentState.playerAuthId,
        invitationPlayerId:
          invitationState.currentState.invitationPlayerId || authStore.currentState.playerAuthId || getPersistentPlayerId(),
        isAppleDevice: isAppleDevice(),
        isMobileDevice: isMobileDevice(),
      },
      iceServers: iceServers || undefined,
      forceTurn: !playerConnectionState.currentState.allowPeerConnections,
    },
    playerId,
  );
  peer.connect(hostBroadcastId);

  try {
    (window as any).playcast = (window as any).playcast || {};
    (window as any).playcast.peer = peer;
    (window as any).playcast.qualityManager = qualityManager;

    // Add audio debugging function
    (window as any).playcast.debugAudio = () => {
      const video = document.querySelector('video');
      const audio = document.querySelector('audio');
      const streams = mediaStreamState.currentState;

      console.log('🎵 === AUDIO DEBUG INFO ===');
      console.log('Video element:', {
        present: !!video,
        muted: video?.muted,
        volume: video?.volume,
        paused: video?.paused,
        readyState: video?.readyState,
        srcObject: video?.srcObject ? 'present' : 'missing'
      });

      console.log('Audio element:', {
        present: !!audio,
        muted: audio?.muted,
        volume: audio?.volume,
        paused: audio?.paused,
        readyState: audio?.readyState,
        srcObject: audio?.srcObject ? 'present' : 'missing'
      });

      console.log('MediaStream state:', {
        mainStream: streams.stream ? {
          id: streams.stream.id,
          videoTracks: streams.stream.getVideoTracks().length,
          audioTracks: streams.stream.getAudioTracks().length,
          audioTrackDetails: streams.stream.getAudioTracks().map(t => ({
            id: t.id,
            label: t.label,
            enabled: t.enabled,
            readyState: t.readyState
          }))
        } : 'missing',
        audioStream: streams.audioStream ? {
          id: streams.audioStream.id,
          audioTracks: streams.audioStream.getAudioTracks().length,
          audioTrackDetails: streams.audioStream.getAudioTracks().map(t => ({
            id: t.id,
            label: t.label,
            enabled: t.enabled,
            readyState: t.readyState
          }))
        } : 'missing'
      });

      console.log('🎵 === END AUDIO DEBUG ===');
    };

    console.log('🔧 Added window.playcast.debugAudio() - call this to debug audio issues');
  } catch (e) {
    console.error('Could not set window.playcast', e);
  }

  let statsCollectionArray: WebrtcQualityStatsMetric[] = [];

  const checkStatus = (status: PlaycastClientPeerStatus) => {
    console.warn('Status:', status);
    if (status.connectionState === 'closed' || status.connectionState === 'refused') {
      const wasRefused = status.connectionState === 'refused';
      try {
        recordDisconnectReason(status.kickReason ?? 'unknown', 'marketing:peerStatus');
      } catch (err) {
        console.warn('[Marketing] Failed to record status close reason', err);
      }
      playerConnectionState.updateState({
        ...playerConnectionState.currentState,
        status: wasRefused ? 'refused' : 'disconnected',
        kickReason: status.kickReason,
      });
      if (statsCollectionArray.length > 0) {
        sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
        realtime.sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
        statsCollectionArray = [];
      }
    }
    if (status.connectionState === 'connected') {
      playerConnectionState.updateState({
        ...playerConnectionState.currentState,
        status: 'connected',
      });
    }
  };
  peer.status.addListener((status) => {
    checkStatus(status);
  });
  checkStatus(peer.status.currentState);
  let debounceTimeout: NodeJS.Timeout | null = null;
  peer.incomingTracks.addListener((tracks) => {
    // Return early if we already have a pending update
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Check device type for optimal stream handling
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;

    debounceTimeout = setTimeout(() => {
      console.log('Incoming tracks (debounced):', tracks);

      sendVideoTransitTimingData('player_receive');
      const allTracks = Object.values(tracks).map((t) => t.track);
      const videoTracks = allTracks.filter((t) => t.kind === 'video');
      const audioTracks = allTracks.filter((t) => t.kind === 'audio');

      // Get current streams
      const currentStream = mediaStreamState.currentState.stream;
      const currentAudioStream = mediaStreamState.currentState.audioStream;

      // For mobile devices (iOS/Android) or when streams don't exist, create new streams
      // This prevents audio/video disruption issues on mobile browsers
      if (isMobile || !currentStream || !currentAudioStream) {
        console.log('Creating new MediaStreams for mobile device or missing streams');
        const newStream = new MediaStream();
        const newAudioStream = new MediaStream();

        // Add all tracks to the new streams
        videoTracks.forEach((track) => newStream.addTrack(track));
        audioTracks.forEach((track) => {
          newStream.addTrack(track);
          newAudioStream.addTrack(track);
        });

        // Update media stream state with new streams
        mediaStreamState.updateState({
          stream: newStream,
          audioStream: newAudioStream,
        });
      } else {
        // For desktop devices, use smarter track management to minimize disruption
        const currentVideoTracks = currentStream.getVideoTracks();
        const currentAudioTracks = currentAudioStream.getAudioTracks();

        // Check if we actually need to update tracks (avoid unnecessary changes)
        const videoTracksChanged =
          currentVideoTracks.length !== videoTracks.length ||
          !videoTracks.every(track => currentVideoTracks.some(ct => ct.id === track.id));

        const audioTracksChanged =
          currentAudioTracks.length !== audioTracks.length ||
          !audioTracks.every(track => currentAudioTracks.some(ct => ct.id === track.id));

        if (videoTracksChanged || audioTracksChanged) {
          console.log('🔄 Updating tracks on desktop - video changed:', videoTracksChanged, 'audio changed:', audioTracksChanged);

          if (audioTracksChanged) {
            console.log('🎵 Audio track changes detected:');
            console.log('  Current audio tracks:', currentAudioTracks.map(t => ({ id: t.id, label: t.label, enabled: t.enabled })));
            console.log('  New audio tracks:', audioTracks.map(t => ({ id: t.id, label: t.label, enabled: t.enabled })));
          }

          // Only update video tracks if they changed
          if (videoTracksChanged) {
            currentVideoTracks.forEach((track) => currentStream.removeTrack(track));
            videoTracks.forEach((track) => currentStream.addTrack(track));
          }

          // Only update audio tracks if they changed
          if (audioTracksChanged) {
            console.log('🗑️ Removing old audio tracks...');
            // Be more careful with audio track changes to prevent disruption
            currentAudioTracks.forEach((track) => {
              console.log(`  Removing track ${track.id} (${track.label})`);
              currentStream.removeTrack(track);
              currentAudioStream.removeTrack(track);
              // Stop the track to ensure proper cleanup
              track.stop();
            });

            console.log('➕ Adding new audio tracks...');
            audioTracks.forEach((track) => {
              console.log(`  Adding track ${track.id} (${track.label}), enabled: ${track.enabled}`);
              currentStream.addTrack(track);
              currentAudioStream.addTrack(track);
            });

            // Log final state
            console.log('🎵 Final audio stream tracks:', currentAudioStream.getAudioTracks().map(t => ({ id: t.id, label: t.label, enabled: t.enabled })));
          }

          // Trigger state update only if something actually changed
          mediaStreamState.updateState({
            stream: currentStream,
            audioStream: currentAudioStream,
          });
        } else {
          console.log('✅ No track changes detected, skipping update');
        }
      }

      debounceTimeout = null;
    }, isMobile ? 200 : 500); // Shorter debounce for mobile to reduce latency
  });
  peer.onMessage((msg) => {
    handleHostMessage(msg);
  });
  setInterval(() => {
    // log info about the audio track.
    const stream = mediaStreamState.currentState.audioStream;
    const videoStream = mediaStreamState.currentState.stream;

    console.log('🎵 Audio Stream Debug:');
    if (stream) {
      const audioTracks = stream.getAudioTracks();
      console.log(`  Audio tracks in audioStream: ${audioTracks.length}`);
      audioTracks.forEach((track, i) => {
        console.log(`    Track ${i}: ${track.id} (${track.label}) - enabled: ${track.enabled}, ready: ${track.readyState}`);
      });
    } else {
      console.log('  No audioStream');
    }

    if (videoStream) {
      const audioTracksInVideoStream = videoStream.getAudioTracks();
      console.log(`  Audio tracks in main stream: ${audioTracksInVideoStream.length}`);
      audioTracksInVideoStream.forEach((track, i) => {
        console.log(`    Track ${i}: ${track.id} (${track.label}) - enabled: ${track.enabled}, ready: ${track.readyState}`);
      });
    }
  }, 5000);

  peer.statistics.addListener((stats) => {
    console.log('Quality stats:', stats.quality);
    qualityManager.parseQualityStats(stats.quality);

    // Extract decode timing data from RTP stats
    const rtpTimingData = extractPlayerRtpTimingData(stats.rtp);

    // Combine quality stats with RTP timing data
    const combinedRawStats = {
      // Start with quality management stats (legacy fields)
      ...stats.quality.rawStats,
      // Add timing data from RTP stats (player focuses on inbound/decode data)
      ...rtpTimingData,
    };

    statsCollectionArray.push({
      recommendation: stats.quality.recommendation,
      reason: stats.quality.reason,
      rawStats: combinedRawStats,
    });

    if (statsCollectionArray.length >= 10) {
      sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
      realtime.sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
      statsCollectionArray = [];
    }
  });

  return peer;
};

/**
 * Extracts timing data from player RTP stats (focuses on inbound/decode data)
 */
const extractPlayerRtpTimingData = (rtpStats: { inbound: Array<Record<string, any>>; outbound: Array<Record<string, any>> }) => {
  const timingData: Record<string, number> = {};

  console.log('[Player] Extracting RTP timing data:', {
    inboundCount: rtpStats.inbound?.length || 0,
    outboundCount: rtpStats.outbound?.length || 0,
    inboundStreams: rtpStats.inbound?.map((stream, i) => ({
      index: i,
      ssrc: stream.ssrc,
      type: stream.type,
      hasDecodeTime: stream.averageDecodeTimePerFrameMs !== undefined && stream.averageDecodeTimePerFrameMs > 0,
      hasProcessingDelay: stream.averageProcessingDelayPerFrameMs !== undefined && stream.averageProcessingDelayPerFrameMs > 0,
      avgDecodeTime: stream.averageDecodeTimePerFrameMs,
      avgProcessingDelay: stream.averageProcessingDelayPerFrameMs,
      totalDecodeTime: stream.totalDecodeTime,
      framesDecoded: stream.framesDecoded,
      codecName: stream.codec?.name,
      frameWidth: stream.frameWidth,
      frameHeight: stream.frameHeight
    })) || []
  });

  // Extract from inbound (decoding) stats - player perspective
  if (rtpStats.inbound && rtpStats.inbound.length > 0) {
    let foundDecodingData = false;

    for (let i = 0; i < rtpStats.inbound.length; i++) {
      const inbound = rtpStats.inbound[i];
      console.log(`[Player] Checking inbound stream ${i}:`, {
        ssrc: inbound.ssrc,
        averageDecodeTimePerFrameMs: inbound.averageDecodeTimePerFrameMs,
        averageProcessingDelayPerFrameMs: inbound.averageProcessingDelayPerFrameMs,
        totalDecodeTime: inbound.totalDecodeTime,
        framesDecoded: inbound.framesDecoded,
        frameWidth: inbound.frameWidth,
        frameHeight: inbound.frameHeight,
        hasDecodingData: (inbound.averageDecodeTimePerFrameMs && inbound.averageDecodeTimePerFrameMs > 0) ||
                        (inbound.averageProcessingDelayPerFrameMs && inbound.averageProcessingDelayPerFrameMs > 0) ||
                        (inbound.totalDecodeTime && inbound.totalDecodeTime > 0) ||
                        (inbound.framesDecoded && inbound.framesDecoded > 0)
      });

      // Check if this stream has meaningful decoding timing data (greater than 0)
      if ((inbound.averageDecodeTimePerFrameMs && inbound.averageDecodeTimePerFrameMs > 0) ||
          (inbound.averageProcessingDelayPerFrameMs && inbound.averageProcessingDelayPerFrameMs > 0) ||
          (inbound.totalDecodeTime && inbound.totalDecodeTime > 0) ||
          (inbound.framesDecoded && inbound.framesDecoded > 0)) {

        timingData.averageDecodeTimePerFrameMs = inbound.averageDecodeTimePerFrameMs || 0;
        timingData.averageProcessingDelayPerFrameMs = inbound.averageProcessingDelayPerFrameMs || 0;
        timingData.totalDecodeTime = inbound.totalDecodeTime || 0;
        timingData.totalProcessingDelay = inbound.totalProcessingDelay || 0;
        timingData.framesDecoded = inbound.framesDecoded || 0;
        timingData.jitterBufferEmittedCount = inbound.jitterBufferEmittedCount || 0;
        foundDecodingData = true;
        console.log(`[Player] ✅ Found decoding data in stream ${i}:`, {
          averageDecodeTimePerFrameMs: timingData.averageDecodeTimePerFrameMs,
          averageProcessingDelayPerFrameMs: timingData.averageProcessingDelayPerFrameMs,
          totalDecodeTime: timingData.totalDecodeTime,
          framesDecoded: timingData.framesDecoded
        });
        break; // Found the stream with decoding data, use it
      }
    }

    // If no stream had decoding data, set defaults
    if (!foundDecodingData) {
      timingData.averageDecodeTimePerFrameMs = 0;
      timingData.averageProcessingDelayPerFrameMs = 0;
      timingData.totalDecodeTime = 0;
      timingData.totalProcessingDelay = 0;
      timingData.framesDecoded = 0;
      timingData.jitterBufferEmittedCount = 0;
      console.log('[Player] ❌ No decoding data found in any inbound stream, using defaults');
    }
  }

  return timingData;
};

export const playerDisconnect = () => {
  // Close the peer connection first
  const currentPeer = getPeer();
  if (currentPeer) {
    console.log('[Peer] Player disconnecting, closing peer connection');
    currentPeer.close('player:close:manual_disconnect');
  }

  // Update join state
  joinState.updateState({
    ...joinState.currentState,
    state: 'playerDisconnected',
  });
};

export const getPeer = (): PlaycastPeerClient | undefined => {
  return peer;
};

export const devHardResetVideo = () => {
  // reconnecting = true;
  // createPeer(joinIdState.currentState);
  const panicMessage = generators.peer.frameratePanic('player', peer.peerId, true);
  peer.sendMessage(panicMessage);
};
